
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function AboutPage() {
  const { t } = useTranslation();

  return (
    <>
      <SEOHead 
        title={t('seo.about.title')}
        description={t('seo.about.description')}
      />
      <div className="min-h-screen bg-black text-white">
        <Navigation />
        
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-gray-900 to-black text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: '"Orbitron", sans-serif' }}>{t('about.hero_title')}</h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                {t('about.hero_subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <img 
                  src="https://static.wixstatic.com/media/4b4a97_435e8ae2f3134cb7bf1f172838922b39~mv2.jpg"
                  alt="Jean-Claude Bastos in the studio"
                  className="w-full h-[600px] object-cover object-top rounded-lg shadow-xl"
                />
              </div>
              
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Orbitron", sans-serif' }}>{t('about.main_title')}</h2>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                    {t('about.main_text_1')}
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                    {t('about.main_text_2')}
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                    {t('about.main_text_3')}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-6 rounded-lg border border-cyan-100">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-cyan-500 text-white rounded-full mr-3">
                        <i className="ri-music-2-line text-lg"></i>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: '"Rajdhani", sans-serif' }}>{t('about.music_style')}</h3>
                    </div>
                    <p className="text-gray-600" style={{ fontFamily: '"Exo 2", sans-serif' }}>{t('about.music_style_desc')}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-100">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-purple-500 text-white rounded-full mr-3">
                        <i className="ri-map-pin-2-line text-lg"></i>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: '"Rajdhani", sans-serif' }}>{t('about.based_in')}</h3>
                    </div>
                    <p className="text-gray-600" style={{ fontFamily: '"Exo 2", sans-serif' }}>{t('about.based_in_desc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quote Section */}
        <section 
          className="py-20 bg-cover bg-center bg-no-repeat relative"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('https://static.wixstatic.com/media/4b4a97_41df6394a28640d797e335d484e8beb6~mv2.png')`
          }}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="text-white">
              <i className="ri-double-quotes-l text-6xl text-cyan-400 mb-8 block"></i>
              <blockquote className="text-3xl font-light mb-8 leading-relaxed" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                "{t('about.quote')}"
              </blockquote>
              <cite className="text-cyan-300 text-xl font-semibold" style={{ fontFamily: '"Orbitron", sans-serif' }}>{t('about.quote_author')}</cite>
            </div>
          </div>
        </section>

        {/* Musical Journey */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Orbitron", sans-serif' }}>{t('about.journey_title')}</h2>
              <p className="text-xl text-gray-600" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                {t('about.journey_subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: t('about.journey_1_title'),
                  description: t('about.journey_1_desc'),
                  icon: 'ri-seedling-line',
                  image: 'https://public.readdy.ai/ai/img_res/65bf4607e23e596e020b66335b978cf1.jpg'
                },
                {
                  title: t('about.journey_2_title'),
                  description: t('about.journey_2_desc'),
                  icon: 'ri-rocket-line',
                  image: 'https://static.wixstatic.com/media/4b4a97_648fe0b8a81c4d84954df56978ceb610~mv2.jpg',
                },
                {
                  title: t('about.journey_3_title'),
                  description: t('about.journey_3_desc'),
                  icon: 'ri-telescope-line',
                  image: 'https://readdy.ai/api/search-image?query=Future%20of%20electronic%20music%2C%20innovative%20production%2C%20cutting-edge%20studio%20technology%2C%20visionary%20music%20creation%2C%20modern%20electronic%20music%20producer%2C%20futuristic%20sound%20design&width=400&height=200&seq=journey-future&orientation=landscape'
                }
              ].map((stage, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <img 
                    src={stage.image} 
                    alt={stage.title}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="p-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 text-white rounded-full mb-4">
                      <i className={`${stage.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: '"Rajdhani", sans-serif' }}>{stage.title}</h3>
                    <p className="text-gray-600" style={{ fontFamily: '"Exo 2", sans-serif' }}>{stage.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Official Website CTA */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white rounded-lg p-12">
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: '"Orbitron", sans-serif' }}>{t('about.website_cta_title')}</h2>
              <p className="text-xl text-cyan-100 mb-8" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                {t('about.website_cta_desc')}
              </p>
              <a 
                href="https://www.jeanclaudebastosmusic.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-cyan-500 hover:bg-gray-100 px-8 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center" style={{ fontFamily: '"Rajdhani", sans-serif' }}
              >
                <i className="ri-external-link-line mr-2"></i>
                jeanclaudebastosmusic.com
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
